import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { trackUsage } from './telemetry';

declare global {
  interface Window {
    grantIgnacioAdmin?: () => Promise<any>;
  }
}

if (process.env.NODE_ENV !== 'production') {
  window.grantIgnacioAdmin = async () => {
    const email = '1992ignacio@gmail.com';
    trackUsage('usage_admin_claim_debug', { action: 'grant_admin_request', email }).catch(
      console.warn
    );
    const grantAdminFn = httpsCallable<{ email: string }, any>(functions, 'grantAdminByEmail');
    const result = await grantAdminFn({ email });
    trackUsage('usage_admin_claim_debug', { action: 'grant_admin_success', email }).catch(
      console.warn
    );
    return result.data;
  };
}

export interface AdminConfig {
  maxDailyUploadBytes: number;
  maxDailyUploadCount: number;
  archiveYears: number;
  enableStripeTax: boolean;
  flags: Record<string, boolean>;
}

export interface UserFlagUpdate {
  uid: string;
  onHold?: boolean;
  legalHold?: {
    enabled: boolean;
    scope: 'ALL' | 'YEAR';
    year?: number;
    note?: string;
  };
}

export interface MetricsData {
  date: string;
  totalUsers: number;
  totalViews: number;
  totalActions: number;
  totalDwellMs: number;
  totalUploadBytes: number;
  deviceBreakdown: Record<string, number>;
  planBreakdown: Record<string, number>;
  topPages: Record<string, number>;
  topActions: Record<string, number>;
  processedAt: string;
  processedBy: string;
}

/**
 * SDK wrapper for admin functions
 */
export class AdminSDK {
  /**
   * Grant admin privileges to a user by email (bootstrap function)
   */
  static async grantAdminByEmail(email: string): Promise<{
    success: boolean;
    uid: string;
    email: string;
    grantedAt: string;
  }> {
    try {
      const grantAdminFn = httpsCallable<{ email: string }, any>(functions, 'grantAdminByEmail');

      const result = await grantAdminFn({ email });

      trackUsage('admin_action', {
        action: 'grant_admin',
        targetEmail: email,
        success: true,
      });

      return result.data;
    } catch (error) {
      trackUsage('admin_action', {
        action: 'grant_admin',
        targetEmail: email,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Set global configuration values
   */
  static async setGlobalConfig(config: AdminConfig): Promise<{
    success: boolean;
    config: AdminConfig;
    updatedAt: string;
  }> {
    try {
      const setConfigFn = httpsCallable<AdminConfig, any>(functions, 'setGlobalConfig');

      const result = await setConfigFn(config);

      trackUsage('admin_action', {
        action: 'set_global_config',
        changes: Object.keys(config),
        success: true,
      });

      return result.data;
    } catch (error) {
      trackUsage('admin_action', {
        action: 'set_global_config',
        changes: Object.keys(config),
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Update user flags (hold, legal hold, etc.)
   */
  static async setUserFlag(update: UserFlagUpdate): Promise<{
    success: boolean;
    uid: string;
    changes: any;
    updatedAt: string;
  }> {
    try {
      const setUserFlagFn = httpsCallable<UserFlagUpdate, any>(functions, 'adminSetUserFlag');

      const result = await setUserFlagFn(update);

      trackUsage('admin_action', {
        action: 'set_user_flag',
        targetUser: update.uid,
        changes: Object.keys(update).filter(k => k !== 'uid'),
        success: true,
      });

      return result.data;
    } catch (error) {
      trackUsage('admin_action', {
        action: 'set_user_flag',
        targetUser: update.uid,
        changes: Object.keys(update).filter(k => k !== 'uid'),
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Generate signed URL for admin receipt preview
   */
  static async getSignedReceiptUrl(
    uid: string,
    path: string
  ): Promise<{
    success: boolean;
    signedUrl: string;
    expiresIn: string;
    path: string;
  }> {
    try {
      const getSignedUrlFn = httpsCallable<{ uid: string; path: string }, any>(
        functions,
        'adminSignedReceiptUrl'
      );

      const result = await getSignedUrlFn({ uid, path });

      trackUsage('admin_action', {
        action: 'preview_receipt',
        targetUser: uid,
        success: true,
      });

      return result.data;
    } catch (error) {
      trackUsage('admin_action', {
        action: 'preview_receipt',
        targetUser: uid,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Build daily metrics for a specific date
   */
  static async buildDailyMetrics(date?: string): Promise<MetricsData> {
    try {
      const buildMetricsFn = httpsCallable<{ date?: string }, MetricsData>(
        functions,
        'buildDailyMetrics'
      );

      const result = await buildMetricsFn({ date });

      trackUsage('admin_action', {
        action: 'build_metrics',
        targetDate: date,
        success: true,
      });

      return result.data;
    } catch (error) {
      trackUsage('admin_action', {
        action: 'build_metrics',
        targetDate: date,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Get current global config (requires admin access)
   */
  static async getGlobalConfig(): Promise<AdminConfig> {
    // This would require a separate callable function to read config
    // For now, return defaults
    return {
      maxDailyUploadBytes: 2_000_000_000, // 2GB
      maxDailyUploadCount: 500,
      archiveYears: 6,
      enableStripeTax: false,
      flags: {
        allowSignups: true,
        abuseQueue: true,
        metrics: true,
      },
    };
  }
}
